import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  X, 
  Smartphone, 
  Star, 
  Flame, 
  CheckCircle, 
  TrendingUp, 
  FileText,
  Send,
  Camera,
  Sparkles,
  Clock,
  MessageSquare
} from "lucide-react";
import { Button } from "./ui/button";

// Simulated chat messages
const simulatedMessages = [
  { id: 1, sender: "ai", content: "¡Hola! Soy el asistente de tu nutricionista. ¿Cómo estuvo tu alimentación hoy?", time: "09:00" },
  { id: 2, sender: "client", content: "¡Hola! Hoy seguí el plan bastante bien 😊", time: "09:15" },
  { id: 3, sender: "ai", content: "¡Excelente! Me alegra escuchar eso. ¿Pudiste cumplir con las 3 comidas principales?", time: "09:16" },
  { id: 4, sender: "client", content: "Sí, desayuné avena con frutas, almorcé pollo con ensalada y cené pescado 🐟", time: "09:20" },
  { id: 5, sender: "ai", content: "¡Perfecto! Eso suena muy balanceado. Tu racha va muy bien, llevas 21 días seguidos. ¡Sigue así! 🔥", time: "09:21" },
  { id: 6, sender: "client", content: "Gracias! Una pregunta, ¿puedo sustituir el arroz por quinoa?", time: "14:30" },
  { id: 7, sender: "ai", content: "¡Claro que sí! La quinoa es una excelente alternativa. Tiene más proteína y fibra que el arroz. Mantén la misma porción (1 taza cocida). 👍", time: "14:31" },
];

// Simulated heatmap data
const generateHeatmap = (streak) => {
  const heatmap = [];
  for (let i = 0; i < 30; i++) {
    const date = new Date();
    date.setDate(date.getDate() - (29 - i));
    heatmap.push({
      date: date.toISOString().split('T')[0],
      completed: i >= (30 - streak - Math.floor(Math.random() * 5))
    });
  }
  return heatmap;
};

const PhonePreview = ({ isOpen, onClose, client }) => {
  const [activeTab, setActiveTab] = useState("checkin");
  const [rating, setRating] = useState(0);
  const [showResponse, setShowResponse] = useState(false);
  const [messages, setMessages] = useState(simulatedMessages);
  const [heatmap, setHeatmap] = useState([]);

  useEffect(() => {
    if (client) {
      setHeatmap(generateHeatmap(client.current_streak || 14));
    }
    setActiveTab("checkin");
    setRating(0);
    setShowResponse(false);
  }, [client]);

  const handleSubmitCheckin = () => {
    if (rating > 0) {
      setShowResponse(true);
    }
  };

  if (!client) return null;

  const renderCheckinTab = () => (
    <AnimatePresence mode="wait">
      {!showResponse ? (
        <motion.div
          key="form"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="p-4 space-y-4"
        >
          <div className="text-center pt-2">
            <h2 className="text-lg font-bold text-[#0F4D40]">
              ¡Hola, {client.name.split(' ')[0]}!
            </h2>
            <p className="text-gray-500 text-sm">Es hora de tu check-in diario</p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-4 shadow-sm">
            <p className="text-sm font-medium text-[#0F4D40] text-center">
              ¿Cómo estuvo tu alimentación hoy?
            </p>

            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className="p-0.5 transition-transform hover:scale-110"
                >
                  <Star
                    className={`w-8 h-8 transition-all ${
                      star <= rating ? 'text-[#E57A4F] fill-current' : 'text-gray-200'
                    }`}
                  />
                </button>
              ))}
            </div>

            <textarea
              placeholder="¿Algo que quieras comentar? (opcional)"
              className="w-full p-3 text-sm border border-gray-200 rounded-xl resize-none focus:outline-none focus:border-[#178A6B]"
              rows={2}
            />

            <button className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 text-sm">
              <Camera className="w-4 h-4" />
              <span>Foto de mi comida</span>
            </button>

            <Button
              onClick={handleSubmitCheckin}
              disabled={rating === 0}
              className="w-full rounded-xl bg-[#178A6B] hover:bg-[#0F4D40] py-5 text-sm font-medium"
            >
              <Send className="w-4 h-4 mr-2" />
              Enviar mi check-in
            </Button>
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="response"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-4 space-y-4"
        >
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="w-16 h-16 bg-[#178A6B]/10 rounded-full flex items-center justify-center mx-auto mb-3"
            >
              <CheckCircle className="w-8 h-8 text-[#178A6B]" />
            </motion.div>
            <h3 className="font-bold text-[#0F4D40]">¡Check-in enviado!</h3>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-[#0F4D40] rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-medium text-[#0F4D40] text-sm mb-1">Tu Nutricionista</p>
                <p className="text-gray-700 text-sm leading-relaxed">
                  ¡Excelente trabajo hoy! Tu dedicación está dando frutos. Llevas {client.current_streak + 1} días seguidos. ¡Sigue así! 🔥
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#E57A4F]/10 to-[#F59E0B]/10 border border-[#E57A4F]/20 rounded-2xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Flame className="w-6 h-6 text-[#E57A4F]" />
              <span className="text-3xl font-bold text-[#0F4D40]">{client.current_streak + 1}</span>
            </div>
            <p className="text-gray-600 text-sm">días consecutivos</p>
          </div>

          <div className="text-center text-gray-500 text-xs flex items-center justify-center gap-1">
            <Clock className="w-3 h-3" />
            <span>Próximo check-in: mañana a las 9:00</span>
          </div>

          <Button
            onClick={() => setShowResponse(false)}
            variant="outline"
            className="w-full rounded-xl border-gray-200 text-gray-600"
          >
            Hacer otro check-in
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const renderProgressTab = () => (
    <div className="p-4 space-y-4">
      <div className="bg-gradient-to-br from-[#E57A4F]/10 to-[#F59E0B]/10 border border-[#E57A4F]/20 rounded-2xl p-4 text-center">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Flame className="w-8 h-8 text-[#E57A4F]" />
          <span className="text-4xl font-bold text-[#0F4D40]">{client.current_streak}</span>
        </div>
        <p className="text-gray-600 text-sm font-medium">días consecutivos</p>
        <p className="text-gray-400 text-xs mt-1">Mejor racha: {client.best_streak} días</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
        <h3 className="font-semibold text-[#0F4D40] mb-3 text-sm">Últimos 30 días</h3>
        <div className="grid grid-cols-10 gap-1">
          {heatmap.map((day, index) => (
            <div
              key={index}
              className={`aspect-square rounded-sm ${
                day.completed ? 'bg-[#178A6B]' : 'bg-gray-100'
              }`}
              title={day.date}
            />
          ))}
        </div>
        <div className="flex items-center justify-center gap-4 mt-3 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-gray-100 rounded-sm"></div>
            <span>Sin check-in</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-[#178A6B] rounded-sm"></div>
            <span>Completado</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
        <h3 className="font-semibold text-[#0F4D40] mb-2 text-sm">Mi Objetivo</h3>
        <p className="text-gray-700 text-sm">{client.goal}</p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white rounded-xl border border-gray-100 p-3 text-center shadow-sm">
          <p className="text-xl font-bold text-[#0F4D40]">{client.total_checkins || 45}</p>
          <p className="text-xs text-gray-500">Check-ins</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3 text-center shadow-sm">
          <div className="flex items-center justify-center gap-1">
            <Star className="w-4 h-4 text-[#F59E0B] fill-current" />
            <p className="text-xl font-bold text-[#0F4D40]">4.5</p>
          </div>
          <p className="text-xs text-gray-500">Promedio</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3 text-center shadow-sm">
          <p className="text-xl font-bold text-[#178A6B]">87%</p>
          <p className="text-xs text-gray-500">Adherencia</p>
        </div>
      </div>
    </div>
  );

  const renderChatTab = () => (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.map((msg) => (
          <div 
            key={msg.id}
            className={`flex ${msg.sender === 'client' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[80%] p-3 rounded-2xl ${
              msg.sender === 'client' 
                ? 'bg-[#178A6B] text-white rounded-br-md' 
                : 'bg-gray-100 text-gray-800 rounded-bl-md'
            }`}>
              <p className="text-sm">{msg.content}</p>
              <p className={`text-xs mt-1 ${msg.sender === 'client' ? 'text-white/70' : 'text-gray-400'}`}>
                {msg.time}
              </p>
            </div>
          </div>
        ))}
      </div>
      <div className="p-3 border-t border-gray-100 bg-white">
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Escribe un mensaje..."
            className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-full focus:outline-none focus:border-[#178A6B]"
          />
          <Button size="icon" className="rounded-full bg-[#178A6B] hover:bg-[#0F4D40] w-9 h-9">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  const renderPlanTab = () => (
    <div className="p-4 space-y-4">
      <div className="bg-white rounded-2xl border border-[#178A6B] p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-[#0F4D40] text-sm">Plan Activo</h3>
          <span className="text-xs bg-[#178A6B] text-white px-2 py-0.5 rounded-full">Activo</span>
        </div>
        <p className="text-xs text-gray-500 mb-3">Plan personalizado para {client.goal}</p>
        
        <div className="space-y-2">
          {[
            { meal: "Desayuno", foods: "Avena con frutas, huevos", calories: 450 },
            { meal: "Almuerzo", foods: "Pollo, arroz integral, ensalada", calories: 650 },
            { meal: "Cena", foods: "Pescado al vapor, verduras", calories: 500 },
            { meal: "Snacks", foods: "Frutos secos, yogurt", calories: 200 },
          ].map((item, i) => (
            <div key={i} className="bg-gray-50 p-2.5 rounded-xl">
              <div className="flex justify-between items-center mb-1">
                <p className="font-medium text-gray-700 text-xs">{item.meal}</p>
                <p className="text-xs text-[#178A6B]">{item.calories} kcal</p>
              </div>
              <p className="text-xs text-gray-500">{item.foods}</p>
            </div>
          ))}
        </div>

        <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center">
          <span className="text-xs text-gray-500">Total diario</span>
          <span className="font-bold text-[#0F4D40]">1,800 kcal</span>
        </div>
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />
          
          {/* Phone Frame */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 50 }}
            transition={{ type: "spring", damping: 25 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50"
          >
            {/* Phone container */}
            <div className="relative">
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center z-10 hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>

              {/* Phone frame */}
              <div className="w-[320px] h-[640px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
                {/* Inner phone */}
                <div className="w-full h-full bg-[#FAFAFA] rounded-[2.5rem] overflow-hidden relative">
                  {/* Status bar */}
                  <div className="h-10 bg-white flex items-center justify-between px-6 text-xs">
                    <span className="font-medium">9:41</span>
                    <div className="absolute left-1/2 -translate-x-1/2 w-20 h-6 bg-black rounded-full"></div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-2 border border-gray-400 rounded-sm">
                        <div className="w-3/4 h-full bg-green-500 rounded-sm"></div>
                      </div>
                    </div>
                  </div>

                  {/* App header */}
                  <div className="bg-white border-b border-gray-100 px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-[#0F4D40] rounded-lg flex items-center justify-center">
                          <Sparkles className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-500">Asistente de</p>
                          <p className="font-semibold text-[#0F4D40] text-xs">Tu Nutricionista</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 bg-[#E57A4F]/10 px-2 py-1 rounded-full">
                        <Flame className="w-3 h-3 text-[#E57A4F]" />
                        <span className="font-bold text-[#0F4D40] text-xs">{client.current_streak}</span>
                      </div>
                    </div>
                  </div>

                  {/* Content area */}
                  <div className="h-[calc(100%-140px)] overflow-y-auto">
                    {activeTab === "checkin" && renderCheckinTab()}
                    {activeTab === "progress" && renderProgressTab()}
                    {activeTab === "chat" && renderChatTab()}
                    {activeTab === "plan" && renderPlanTab()}
                  </div>

                  {/* Bottom navigation */}
                  <div className="absolute bottom-0 left-0 right-0 h-16 bg-white border-t border-gray-100 grid grid-cols-4">
                    {[
                      { key: "checkin", icon: CheckCircle, label: "Check-in" },
                      { key: "progress", icon: TrendingUp, label: "Progreso" },
                      { key: "chat", icon: MessageSquare, label: "Chat" },
                      { key: "plan", icon: FileText, label: "Plan" },
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`flex flex-col items-center justify-center gap-0.5 transition-colors ${
                          activeTab === tab.key ? "text-[#0F4D40]" : "text-gray-400"
                        }`}
                      >
                        <tab.icon className="w-5 h-5" />
                        <span className="text-[10px] font-medium">{tab.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Label */}
              <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-2 text-white/80">
                <Smartphone className="w-4 h-4" />
                <span className="text-sm font-medium">Vista del cliente: {client.name}</span>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PhonePreview;
